import Cookies from 'js-cookie';

import TagManagerProvider from './TagManagerProvider';
import ImpactProvider from './ImpactProvider';
import { type OneOfPaymentMethods, paymentMethodTypes } from '@/utils/stripe/paymentApi';
import { type Plan } from '@/utils/mapProductObject';

const paymentMethodToAnalytics = (paymentMethod: string) => {
  if (paymentMethod === paymentMethodTypes.card) {
    return 'card';
  }
  if (paymentMethod === paymentMethodTypes.googlePay) {
    return 'googlepay';
  }
  if (paymentMethod === paymentMethodTypes.applePay) {
    return 'applepay';
  }

  return paymentMethod;
};

const PLANS_LOADED = 'checkout-loaded';
const CHECKOUT_STARTED = 'checkout-started';
const TOGGLE_3_MO_CHANGED = '3mo_toggle_changed';

const AUTH_PROVIDER_STORAGE_KEY = 'auth_provider';
type OAuthProviders = 'google' | 'apple-id' | 'setapp';
const OTC_AUTH_PROVIDER = 'otc';

class AnalyticsEventHandler {
  protected tagMangerProvider: TagManagerProvider;
  protected impactProvider: ImpactProvider;
  protected completedEvetns: string[];

  constructor() {
    this.tagMangerProvider = new TagManagerProvider();
    this.impactProvider = new ImpactProvider();
    this.completedEvetns = [];
  }

  init(): void {
    this.impactProvider.init();
  }

  trackLoaded(): void {
    if (this.completedEvetns.includes(PLANS_LOADED)) return;

    this.completedEvetns.push(PLANS_LOADED);

    this.tagMangerProvider.trackEcommerceEvent({
      event: 'clear-checkout',
      'clear-eventAction': 'Checkout',
      'clear-eventLabel': 'Loaded',
      ecommerce: {
        checkout: {
          actionField: { step: '1', action: 'checkout' },
        },
      },
    });
  }

  trackStarted(paymentMethod: OneOfPaymentMethods): void {
    if (this.completedEvetns.includes(CHECKOUT_STARTED)) return;

    this.completedEvetns.push(CHECKOUT_STARTED);

    this.tagMangerProvider.trackEcommerceEvent({
      event: 'clear-checkout',
      'clear-eventAction': 'Checkout',
      'clear-eventLabel': 'Started',
      ecommerce: {
        checkout: {
          actionField: { step: '2', action: 'checkout', option: paymentMethodToAnalytics(paymentMethod) },
        },
      },
    });
  }

  trackStartedWithCard() {
    this.trackStarted(paymentMethodTypes.card);
  }

  trackStartedWithApplePay() {
    this.trackStarted(paymentMethodTypes.applePay);
  }

  trackStartedWithGooglePay() {
    this.trackStarted(paymentMethodTypes.googlePay);
  }

  trackCheckout(selectedPlan: Plan, paymentMethod: OneOfPaymentMethods) {
    // for cases, when ApplePay was preselected, send 'Started' right before 'Submitted'
    if (!this.completedEvetns.includes(CHECKOUT_STARTED)) this.trackStartedWithApplePay();

    this.tagMangerProvider.trackEcommerceEvent({
      event: 'clear-checkout',
      'clear-eventCategory': 'Ecommerce',
      'clear-eventAction': 'Checkout',
      'clear-eventLabel': 'Submitted',
      ecommerce: {
        checkout: {
          actionField: { step: '3', action: 'checkout', option: paymentMethodToAnalytics(paymentMethod) },
          products: [selectedPlan.analyticsData],
        },
        currencyCode: selectedPlan.currencyCode,
      },
    });
  }

  trackCheckoutWithCard(selectedPlan: Plan) {
    this.trackCheckout(selectedPlan, paymentMethodTypes.card);
  }

  trackCheckoutWithApplePay(selectedPlan: Plan) {
    this.trackCheckout(selectedPlan, paymentMethodTypes.applePay);
  }

  trackCheckoutWithGooglePay(selectedPlan: Plan) {
    this.trackCheckout(selectedPlan, paymentMethodTypes.googlePay);
  }

  trackPurchase(selectedPlan: Plan, transactionId: string, paymentMethod: string, coupon?: string) {
    this.tagMangerProvider.trackEcommerceEvent({
      event: 'clear-purchase',
      'clear-eventAction': 'Purchase',
      'clear-eventLabel': 'Completed',
      ecommerce: {
        currencyCode: selectedPlan.currencyCode,
        purchase: {
          actionField: {
            id: transactionId,
            revenue: selectedPlan.revenue,
            coupon,
            option: paymentMethodToAnalytics(paymentMethod),
          },
          products: [selectedPlan.analyticsData],
        },
      },
    });

    this.impactProvider.trackPurchase();
  }

  trackPurchaseWithCard(selectedPlan: Plan, transactionId: string, coupon?: string) {
    this.trackPurchase(selectedPlan, transactionId, paymentMethodTypes.card, coupon);
  }

  trackPurchaseWithApplePay(selectedPlan: Plan, transactionId: string, coupon?: string) {
    this.trackPurchase(selectedPlan, transactionId, paymentMethodTypes.applePay, coupon);
  }

  trackPurchaseWithGooglePay(selectedPlan: Plan, transactionId: string, coupon?: string) {
    this.trackPurchase(selectedPlan, transactionId, paymentMethodTypes.googlePay, coupon);
  }

  trackPurchaseWithOtherMethods(paymentMethodType: string, selectedPlan: Plan, transactionId: string, coupon?: string) {
    this.trackPurchase(selectedPlan, transactionId, paymentMethodType, coupon);
  }

  trackAlternativeMonetization3moToggle(newValue: boolean) {
    if (this.completedEvetns.includes(TOGGLE_3_MO_CHANGED)) return;

    this.completedEvetns.push(TOGGLE_3_MO_CHANGED);

    this.tagMangerProvider.track({
      event: '3mo_toggle',
      'clear-eventCategory': 'checkout',
      'clear-eventAction': '3mo_toggle_changed',
      'clear-eventLabel': newValue ? 1 : 0,
    });
  }

  // when plans screen is shown, New Order only, PLAN_SCREEN
  trackScreenPlanShown() {
    this.tagMangerProvider.trackCheckoutEvent({
      event: 'screen_plans_shown',
      'clear-eventAction': 'screen_plans_shown',
    });
  }

  // when payment methods screen is shown, New Order only, PAYMENT_METHODS_SCREEN
  trackSubInfoShown(selectedPlanSku: string) {
    this.tagMangerProvider.trackCheckoutEvent({
      event: 'sub_info_shown',
      'clear-eventAction': 'sub_info_shown',
      'clear-eventLabel': selectedPlanSku,
    });
  }

  trackProfile(userId?: string, email?: string) {
    this.impactProvider.trackProfile(userId, email);
  }

  trackGrowthBookTestResult(payload: unknown) {
    this.tagMangerProvider.track(payload as Record<string, string>);
  }

  trackWebPageView() {
    this.tagMangerProvider.track({
      event: 'clearvpn-event',
      eventCategory: 'ClearVPN',
      eventAction: 'Price_page_1',
      eventLabel: 'Show',
    });
  }

  trackWebPageMonthTrialClick() {
    this.tagMangerProvider.track({
      event: 'clearvpn-event',
      eventCategory: 'ClearVPN',
      eventAction: 'Start 3 free days',
      eventLabel: '1-month',
    });
  }

  trackWebPageYearTrialClick() {
    this.tagMangerProvider.track({
      event: 'clearvpn-event',
      eventCategory: 'ClearVPN',
      eventAction: 'Start 3 free days',
      eventLabel: '1-year',
    });
  }

  trackExtensionUserTrialClick(product: string) {
    this.tagMangerProvider.track({
      event: 'clearvpn-event',
      eventCategory: 'chrome_extension',
      eventAction: 'chrome_ext_start_trial_clicked',
      eventLabel: product,
    });
  }

  trackExtensionUserPayByCardClick(product: string) {
    this.tagMangerProvider.track({
      event: 'clearvpn-event',
      eventCategory: 'chrome_extension',
      eventAction: 'chrome_ext_pay_by_card_clicked',
      eventLabel: product,
    });
  }

  logOAuthClick = (provider: OAuthProviders) => {
    Cookies.set(AUTH_PROVIDER_STORAGE_KEY, provider, { domain: '.clearvpn.com' });
  };

  logOTCEmailSubmit() {
    this.tagMangerProvider.logOTCEmailSubmit();
  }

  logOTCCodeSubmitSuccess() {
    this.tagMangerProvider.logOTCCodeSubmitSuccess();

    Cookies.set(AUTH_PROVIDER_STORAGE_KEY, OTC_AUTH_PROVIDER, { domain: '.clearvpn.com' });
  }

  logOTCEmailResend() {
    this.tagMangerProvider.logOTCEmailResend();
  }
}

const analyticsEventHandler = new AnalyticsEventHandler();

export default analyticsEventHandler;
